import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { ContentWrapper } from '../../widgets/ContentWrapper';
import { FAQ } from '../../widgets/FAQ';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import { ListColumns } from '../../widgets/ListColumns';
import Summary from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';
import Text from '../../widgets/Text';
import Image from '../../widgets/Image';

import Section from '../../widgets/Section';
import WhySitePen from '../../widgets/WhySitePen';

const agileImg = require('../../assets/images/logo-illustration/agile-technologies.svg');
const clarifyImg = require('../../assets/images/icon-illustration/clarify.svg');
const controlImg = require('../../assets/images/icon-illustration/control.svg');
const innovationsImg = require('../../assets/images/icon-illustration/innovation.svg');
const likeImg = require('../../assets/images/icon-illustration/like.svg');
const speedImg = require('../../assets/images/icon-illustration/development-speed.svg');
const teamEfficiencyImg = require('../../assets/images/illustration/team-efficiency.svg');

const factory = create();

export const AgileConsulting = factory(function AgileConsulting() {
	return (
		<div>
			<Head
				title="Agile Consulting in Software Development | Sitepen"
				description="SitePen’s agile consulting experts help you and your team respond to market forces and continuously evolve to deliver the right product at the right time."
			/>

			<Hero background="level3Pink">
				{{
					xlTitle: 'Agile Consulting'
				}}
			</Hero>

			<Section>
				<ContentWrapper>
					<WhySitePen reverse>
						{{
							title: 'Agile Consulting with SitePen',
							blurb: (
								<virtual>
									<p>
										Achieving agility is more than instating some popular
										practices. Agility is measured in real outcomes rather than
										checkboxes and involves dynamism and responsiveness to
										market forces. Sitepen has the experience to understand a
										software team’s dynamic and fine tune a process that helps
										it excel, as well as to understand cultural factors that can
										impact the achievement of the desired results.
									</p>

									<p>
										We provide the following as part of our agile consulting
										offering:
									</p>
									<ListColumns
										columnCount={1}
										style="disc"
										paddingLeft="sm"
										paddingBottom="sm"
									>
										<Text type="body">
											Analysis of your current scenario to understand culture,
											opportunities for improvement, and desired outcomes
										</Text>
										<Text type="body">
											Recommendations on short term changes to help your team
											make quick course corrections to get operational and
											productive quickly
										</Text>
										<Text type="body">
											Recommendations on medium term changes to build momentum
											and bring your teams up to a high functioning level
										</Text>
										<Text type="body">
											Training for your teams to ensure shared understanding
											of target practices and culture reset
										</Text>
										<Text type="body">
											On-going coaching to “run beside” the team and assist
											with issues until they are fully independent
										</Text>
									</ListColumns>
									<p>
										We specialize in Scrum, Kanban, and Lean Startup methods,
										and our agile consulting practices will greatly accelerate
										your team’s ability to deliver the high quality products at
										the right moment for the market.
									</p>
								</virtual>
							)
						}}
					</WhySitePen>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Agile Consulting Benefits',
							blurb: (
								<virtual>
									A truly functional agile process not only helps your team level
									up, but gives them the tools to continue evolving and solving
									deep problems so you can reap the benefits of a responsive and
									engaged team.
								</virtual>
							),
							items: [
								{
									title: 'Improved Clarity',
									blurb:
										'Agile consulting helps your team realize added clarity in its web development. At any point in time, the team knows what everyone else is doing and can adjust or collaborate as needed to keep things moving. Obstacles get recognized and solved for quickly, and product managers always know what the team needs of them at any point in time to deliver requested functionality.',
									image: (
										<Image src={clarifyImg} lazy size="icon" align="center" />
									)
								},
								{
									title: 'Faster Deployment',
									blurb:
										'The market evolves too quickly for confidence around the long term releases that were the norm a few years ago. Agile consulting helps your team understand how to identify the most targeted product that is needed to determine viability and get it out the door quickly. Not only that, but because the team is building to high quality standards continuously, you have the flexibility to deploy on increasingly short timeframes, providing a continuous flow of value to your users.',
									image: <Image src={speedImg} lazy size="icon" align="center" />
								},
								{
									title: 'Increased Team Satisfaction',
									blurb:
										'Agile consulting provides the basis for a well implemented agile practice that results in sustainable, regular pacing, provides teams with a predictable meeting cadence, and gives a flatter, more empowered organizational structure. Teams get more satisfaction from a productive work experience, benefit from less burnout, and are able to directly impact their own practices and working agreements. All of this leads to greater morale and more engaged team members who are better able to bring their creativity and productivity to the table.',
									image: <Image src={likeImg} lazy size="icon" align="center" />
								},
								{
									title: 'Accelerate Innovation',
									blurb:
										'When a team builds a product from a set of preconceived requirements, the chances for misses are high.  One of the most powerful aspects of agile methodologies is the feedback gained through rapid iterations of the entire lifecycle: requirements, design, development, and testing. Full, rapid iterations get a working product in the hands of the end user as quickly as possible, allowing for early verification that needs have been met and sparking new ideas about how to best adjust the next goal.',
									image: (
										<Image
											src={innovationsImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Greater Control',
									blurb:
										'Using an agile framework, team members are empowered to collaborate on solutions, bringing their individual strengths to the project. This process sparks building realizations, allowing the team to be greater than the sum of its parts. This also increases buy-in, energy, and engagement across teams, and ultimately results in a better product.',
									image: (
										<Image src={controlImg} lazy size="icon" align="center" />
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<Summary reverse stackedPadding={true}>
						{{
							title: 'Technologies We Use',
							blurb: (
								<virtual>
									<p>
										We use a range of technologies to facilitate collaboration
										and communication within and across teams.
									</p>
									<ListColumns>
										{[
											'Jira',
											'GotoMeeting',
											'Confluence',
											'Google Suite',
											'Slack'
										]}
									</ListColumns>
								</virtual>
							),
							image: <Image src={agileImg} size="lg" lazy />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<IndustryExpertise />
			</Section>
			<Section>
				<ContentWrapper>
					<Summary>
						{{
							title: 'Choosing an Agile Consulting Company',
							blurb: (
								<virtual>
									Many companies have a shallow, rules-based approach to agile
									implementation. SitePen understands that what matters is
									outcomes and team fit. We’re prepared to work with you to
									customize a solution that takes your organizational scenario,
									culture, and team personality into account, and not just offer a
									templated approach. Don’t just “do agile” -{' '}
									<Text uppercase>be</Text> agile.
								</virtual>
							),
							image: <Image src={teamEfficiencyImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="Agile Consulting FAQs">
					{[
						{
							title: 'How Can Agile Consulting Help with Development?',
							content: (
								<p>
									Agile consulting helps teams understand the mindset required to
									effectively use the agile process, as well as the end goals that
									their process should support. As a result, development becomes
									more collaborative and more efficient, with higher quality
									outcomes.
								</p>
							)
						},
						{
							title: 'Why is Agile Consulting Important?',
							content: (
								<p>
									Agile consulting helps teams understand where their
									opportunities lie and how to transform to achieve their goals.
									It gives teams the tools and training they need to implement a
									high-functioning and continuously improving agile process.
								</p>
							)
						},
						{
							title: 'What is the Difference Between Agile and Other Methodologies',
							content: (
								<p>
									Agile provides teams with a lightweight process that supports
									them in collaborating with stakeholders and quickly creating
									valuable, working product increments that keep visibility high.
									It reduces the risks inherent in the waterfall methodology, such
									as out-of-date requirements and cascading schedule impacts, and
									minimizes wasted work when product pivots are needed.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default AgileConsulting;
